import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Container from "@/components/Container";
import Footer from "@/components/Footer";
import Nav from "@/components/Nav";
import { locumColors } from "@/theme";

const legalOnly = import.meta.env.VITE_LEGAL_ONLY === "true";

const sectionSx = { mt: 6 } as const;
const headingSx = {
  mb: 2,
  fontWeight: 700,
  letterSpacing: "-0.01em",
} as const;
const bodySx = {
  color: locumColors.textSecondary,
  lineHeight: 1.7,
  mb: 2,
} as const;

function Section({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <Box sx={sectionSx}>
      <Typography variant="h4" sx={headingSx}>
        {num}. {title}
      </Typography>
      {children}
    </Box>
  );
}

function Clause({ num, children }: { num: string; children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      <Typography
        sx={{
          color: locumColors.muted,
          minWidth: "3.25rem",
          flexShrink: 0,
          lineHeight: 1.7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {num}
      </Typography>
      <Box sx={{ color: locumColors.textSecondary, lineHeight: 1.7, flex: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

function SubItem({
  label,
  indent = 1,
  children,
}: {
  label: string;
  indent?: number;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        mt: 1,
        ml: indent * 3,
      }}
    >
      <Typography
        sx={{
          color: locumColors.muted,
          minWidth: "2.25rem",
          flexShrink: 0,
          lineHeight: 1.7,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ color: locumColors.textSecondary, lineHeight: 1.7, flex: 1 }}>
        {children}
      </Box>
    </Box>
  );
}

function Definition({ term, children }: { term: string; children: ReactNode }) {
  return (
    <Typography sx={{ ...bodySx, ml: { xs: 0, sm: 2 } }}>
      <strong style={{ color: locumColors.textPrimary }}>{term}</strong>{" "}
      {children}
    </Typography>
  );
}

export default function TermsPage() {
  return (
    <>
      {!legalOnly && <Nav />}
      <Box component="main" sx={{ py: { xs: 8, md: 12 }, bgcolor: locumColors.white }}>
        <Container sx={{ maxWidth: "820px !important" }}>
          <Typography
            variant="h2"
            sx={{ mb: 1, fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            Terms of Service
          </Typography>
          <Typography sx={{ color: locumColors.muted, mb: 6 }}>
            LocumPlus Pty Ltd
          </Typography>

          <Typography sx={bodySx}>
            These terms of service are entered into between the operator of the Platform,
            LocumPlus Pty Ltd (ABN 76 681 712 318) and its related companies (together{" "}
            <strong>we</strong>, <strong>us</strong>, <strong>our</strong> and other
            similar expressions) and the relevant subscriber to the Platform (
            <strong>you</strong>, <strong>your</strong> or other similar expressions).
          </Typography>
          <Typography sx={bodySx}>
            Your use of and access to the Platform is subject to these terms of service.
            By clicking &ldquo;I Accept&rdquo;, you indicate that you agree to these
            terms of service.
          </Typography>

          <Section num={1} title="Definitions and interpretation">
            <Clause num="1.1.">
              In these terms of service, the following definitions apply:
              <Box sx={{ mt: 2 }}>
                <Definition term="Additional Services">
                  means any services provided to you in addition to your access to and
                  use of the Platform, including any consulting services or training.
                </Definition>
                <Definition term="Administrator">
                  means an individual who is authorised by a Recruiter to access and use
                  the Platform under its subscriber account and who is nominated by the
                  Recruiter as an Administrator during its registration for a Subscription
                  with the Platform or otherwise during its Subscription with the
                  Platform. A Recruiter may nominate more than one Administrator.
                </Definition>
                <Definition term="Authorised User">
                  means a natural person invited by an Administrator to access and use the
                  Platform under your subscriber account.
                </Definition>
                <Definition term="Candidate">
                  means a subscriber who registers to use the Platform to view Job
                  Listings and engage with Recruiters in order to accept a job or
                  placement (for example &mdash; a locum doctor or substitute teacher).
                </Definition>
                <Definition term="Confidential Information">
                  of a party is information of a party or its customers which the party
                  identifies as confidential or which would reasonably be regarded as
                  confidential and includes without limitation information relating to the
                  party&rsquo;s Intellectual Property Rights, organisational structure,
                  financial position, personnel, policies and business strategies. The
                  Confidential Information of a Recruiter specifically excludes
                  information about any Candidate.
                </Definition>
                <Definition term="Corporations Act">
                  means the <em>Corporations Act 2001</em> (Cth).
                </Definition>
                <Definition term="Data">
                  means the data which is input into the Platform through your account
                  when using the Platform, and includes, if you are a Recruiter, data
                  which is ingested into the Platform through unique email ingestion
                  addresses linked to your account, APIs, and direct website scraping.
                </Definition>
                <Definition term="Dispute">
                  means any dispute, controversy or claim arising out of, relating to or
                  in connection with this document, including without limitation the
                  entry into, existence, breach, termination or validity of this document.
                </Definition>
                <Definition term="Dispute Notice">
                  has the meaning given to that term in clause 27.2.
                </Definition>
                <Definition term="Disputing Party">
                  has the meaning given to that term in clause 27.2.
                </Definition>
                <Definition term="Excess Volume Fee">
                  means, in respect of a Recruiter, the fee payable under that
                  Recruiter&rsquo;s subscription plan when the number of Tasks performed
                  by the Platform through the Recruiter&rsquo;s subscriber account
                  exceeds the Recruiter&rsquo;s Task Volume Cap.
                </Definition>
                <Definition term="Exclusivity Period">
                  means, in respect of a Candidate who subscribes to the Platform for the
                  first time via a link connected with a Recruiter&rsquo;s subscriber
                  account, the period offered by us (in writing) to the Recruiter to be
                  an &lsquo;exclusivity period&rsquo; starting from the date that the
                  Candidate subscribes to the Platform.
                </Definition>
                <Definition term="Fee">
                  means the Subscription Fee, the Excess Volume Fee, the fee payable for
                  any Additional Services (where relevant) and any other fees we may
                  charge under these terms of service.
                </Definition>
                <Definition term="Force Majeure">
                  means any event or circumstance beyond the reasonable control of a
                  party.
                </Definition>
                <Definition term="GST">
                  has the meaning given to that term in the GST Act.
                </Definition>
                <Definition term="GST Act">
                  means the <em>A New Tax System (Goods and Services Tax) Act 1999</em>{" "}
                  (Cth).
                </Definition>
                <Definition term="Indirect Loss">
                  means any one or more of the following:
                </Definition>
                <Box sx={{ ml: { xs: 2, sm: 4 } }}>
                  <SubItem label="(a)" indent={0}>
                    economic loss, loss of actual or anticipated profits, loss of
                    revenue, loss of savings, loss of production, loss of business, loss
                    of opportunity, loss of access to markets, loss of goodwill, loss of
                    reputation, loss of credit, loss of publicity, loss of data, loss of
                    use, loss of interest or loss arising from business interruption; and
                  </SubItem>
                  <SubItem label="(b)" indent={0}>
                    any special, indirect, consequential, incidental, punitive, exemplary
                    or unforeseeable loss or any similar loss.
                  </SubItem>
                </Box>
                <Definition term="Intellectual Property Rights">
                  means all present and future intellectual, industrial or proprietary
                  rights conferred by statute, at common law or in equity anywhere in the
                  world, whether issued or pending, registered or unregistered, including
                  all forms of copyright, patents, trade marks, designs, trade secrets,
                  know-how, confidential information and circuit layouts.
                </Definition>
                <Definition term="Job Listing">
                  means a canonical job or placement listing, which is compiled from
                  various information sources, that is displayed on the Platform (for
                  example &mdash; a part time position as a locum at a medical practice).
                </Definition>
                <Definition term="Personal Information">
                  has the meaning given to that term in the Privacy Act.
                </Definition>
                <Definition term="Platform">
                  means the platform accessible through this website, being a service
                  that enables interactions between Recruiters and Candidates for the
                  posting of Job Listings and filling of jobs and placements in a simple,
                  efficient and secure manner.
                </Definition>
                <Definition term="Privacy Act">
                  means the <em>Privacy Act 1988</em> (Cth), the <em>Spam Act 2003</em>{" "}
                  (Cth) and any other applicable laws relating to privacy and data
                  protection.
                </Definition>
                <Definition term="Recruiter">
                  means a subscriber who registers to use the Platform to post Job
                  Listings on the Platform and engage with Candidates to fill those jobs
                  and placements (for example &mdash; a recruitment agency or an in-house
                  talent acquisition team).
                </Definition>
                <Definition term="Stay">
                  means a stay on enforcing rights against a party which arises under the
                  Corporations Act if:
                </Definition>
                <Box sx={{ ml: { xs: 2, sm: 4 } }}>
                  <SubItem label="(a)" indent={0}>
                    an administrator or a receiver, receiver and manager, judicial
                    manager, liquidator, administrator, controller or like official is
                    appointed to the party or to the whole or a substantial part of the
                    undertaking or property of the party, including any of its assets; or
                  </SubItem>
                  <SubItem label="(b)" indent={0}>
                    it enters into, or takes steps or proposes to enter into, an
                    arrangement, compromise or composition with its creditors or a class
                    of them, or an assignment for the benefit of its creditors or a class
                    of them.
                  </SubItem>
                </Box>
                <Definition term="Subscription">
                  in respect of a Recruiter or Candidate, means the subscription plan
                  (details set out on our pricing page) selected by that Recruiter or
                  Candidate, either during the registration process to gain access to the
                  Platform or during the subscription, which includes the provision of
                  the basic level of support set out in clause 12 of these terms.
                </Definition>
                <Definition term="Subscription Fee">
                  means the fee payable by a Recruiter for its Subscription.
                </Definition>
                <Definition term="Task">
                  means an action the Platform performs for a subscriber, including
                  actions taken when processing a Recruiter&rsquo;s emails and
                  structuring the jobs found within them. For example:
                </Definition>
                <Box sx={{ ml: { xs: 2, sm: 4 } }}>
                  <SubItem label="(a)" indent={0}>
                    an email with 1 job, equals 2 Tasks used (1 for the email and 1 for
                    the job);
                  </SubItem>
                  <SubItem label="(b)" indent={0}>
                    an email with 3 jobs, equals 4 Tasks used (1 for the email and 3 for
                    the jobs); and
                  </SubItem>
                  <SubItem label="(c)" indent={0}>
                    an email with 0 jobs, equals 1 Task used (1 for the email).
                  </SubItem>
                </Box>
                <Definition term="Task Volume Cap">
                  means, in respect of a Recruiter, the maximum number of Tasks specified
                  as being included in that Recruiter&rsquo;s subscription plan.
                </Definition>
              </Box>
            </Clause>
            <Clause num="1.2.">
              In these terms of service, unless the context otherwise requires:
              <SubItem label="(a)">words denoting any gender include all genders;</SubItem>
              <SubItem label="(b)">headings are for convenience only and do not affect interpretation;</SubItem>
              <SubItem label="(c)">the singular includes the plural and vice versa;</SubItem>
              <SubItem label="(d)">any schedule or annexure attached to these terms of service forms part of them;</SubItem>
              <SubItem label="(e)">a reference to a party includes its legal personal representatives, successors and permitted assigns;</SubItem>
              <SubItem label="(f)">a reference to a person includes a corporation, trust, partnership, unincorporated body or other entity, whether or not it comprises a separate legal entity;</SubItem>
              <SubItem label="(g)">a reference to a statute or other law includes regulations and other instruments under it and consolidations, amendments, re-enactments or replacements of any of them;</SubItem>
              <SubItem label="(h)">unless expressly stated to be otherwise, the meaning of general words is not limited by specific examples introduced by &lsquo;including&rsquo;, &lsquo;for example&rsquo; or similar inclusive expressions; and</SubItem>
              <SubItem label="(i)">a reference to these terms of service means this document and includes any variation or replacement of this document.</SubItem>
            </Clause>
          </Section>

          <Section num={2} title="Acceptance of these terms of service">
            <Clause num="2.1.">
              We provide the Platform subject to these terms of service. Before you use
              the Platform, it is important that you read, understand, and agree to these
              terms of service.
            </Clause>
            <Clause num="2.2.">
              You must not use the Platform if you are not of a legal age or capacity to
              form a binding contract with us.
            </Clause>
          </Section>

          <Section num={3} title="Right to use the Platform">
            <Clause num="3.1.">
              Subject to our acceptance of your online registration for provision of the
              Platform and your payment of the relevant Subscription Fee and Excess
              Volume Fee (if any), we grant you a limited, non-exclusive, non-transferable,
              revocable right to access and use the Platform for your personal or internal
              business operations, on the terms set out in these terms of service.
            </Clause>
          </Section>

          <Section num={4} title="Recruiter Access Credentials">
            <Clause num="4.1.">
              We will provide Administrators with a username and password, which will
              enable each Administrator to access and use the Platform on behalf of the
              Recruiter they represent. Each Administrator must select his or her own
              password at the time of first use of the Platform. A Recruiter can add,
              remove or replace your Administrators through your account with the
              Platform.
            </Clause>
            <Clause num="4.2.">
              The Platform allows the Administrators to invite another person to access
              and use the Platform under the Recruiter&rsquo;s subscriber account. The
              Administrators inviting another person to use a Recruiter&rsquo;s subscriber
              account will constitute authorisation of that other person by the Recruiter
              to use its account. A username, linked to the username allocated to the
              Administrators and the Recruiter&rsquo;s subscriber account, will be
              created for each new or additional Authorised User. Each Authorised User
              must select his or her own password at the time of first use of the
              Platform.
            </Clause>
            <Clause num="4.3.">
              A Recruiter must:
              <SubItem label="(a)">ensure that each Administrator and Authorised User keeps his or her username and password confidential and does not disclose it to any other person (and in order to improve the security of the Recruiter&rsquo;s subscriber account, amends his or her password from time to time);</SubItem>
              <SubItem label="(b)">ensure that it can identify and manage each Authorised User entrusted with a username and password combination for the Recruiter&rsquo;s subscriber account;</SubItem>
              <SubItem label="(c)">refrain from authorising anyone to use its subscriber account who is not either employed by the Recruiter or under contract with the Recruiter to perform functions similar to those commonly performed by employees;</SubItem>
              <SubItem label="(d)">notify us of the names of each Authorised User if requested by us; and</SubItem>
              <SubItem label="(e)">immediately notify us of any unauthorised use of an Administrator password or its subscriber account or any other breach of security.</SubItem>
            </Clause>
            <Clause num="4.4.">
              A Recruiter is responsible for all activities that occur under its account,
              including all relevant fees and charges in connection with that use.
            </Clause>
          </Section>

          <Section num={5} title="Candidate Access Credentials">
            <Clause num="5.1.">
              Each Candidate must select his or her own password at the time of first use
              of the Platform.
            </Clause>
            <Clause num="5.2.">
              A Candidate must:
              <SubItem label="(a)">ensure that they keep their username and password confidential and do not disclose it to any other person (and in order to improve the security of their subscriber account, they must amend their password from time to time);</SubItem>
              <SubItem label="(b)">refrain from authorising anyone else to use their subscriber account; and</SubItem>
              <SubItem label="(c)">immediately notify us of any unauthorised use of their password or subscriber account or any other breach of security.</SubItem>
            </Clause>
            <Clause num="5.3.">
              A Candidate is responsible for all activities that occur under their
              account, including all relevant fees and charges in connection with that
              use.
            </Clause>
          </Section>

          <Section num={6} title="Use of the Platform — general">
            <Clause num="6.1.">
              You must ensure that your access and use of the Platform is not illegal or
              prohibited by laws that apply to you.
            </Clause>
            <Clause num="6.2.">
              You must not, nor cause or permit a third party to:
              <SubItem label="(a)">use the Platform in a manner or for a purpose which is improper, immoral or fraudulent, which infringes any person&rsquo;s Intellectual Property Rights, or which restricts or interferes with our provision of the Platform to any other subscribers or users;</SubItem>
              <SubItem label="(b)">reverse engineer, reverse assemble or reverse compile or copy or duplicate or modify or make derivative works of or re-sell all or part of the Platform (or any of our Intellectual Property Rights in the Platform);</SubItem>
              <SubItem label="(c)">
                enter into any transaction relating to:
                <SubItem label="(i)" indent={0}>access or use of the Platform;</SubItem>
                <SubItem label="(ii)" indent={0}>any part of your subscriber account information or access credentials; or</SubItem>
                <SubItem label="(iii)" indent={0}>any of our Intellectual Property Rights in the Platform,</SubItem>
                <Box sx={{ mt: 1 }}>with anyone other than us, without our written consent;</Box>
              </SubItem>
              <SubItem label="(d)">gain or attempt to gain unauthorised access to the Platform, computer systems or networks connected to the Platform, including through hacking, password mining or any other means;</SubItem>
              <SubItem label="(e)">harass, impersonate, stalk or threaten another Recruiter or Candidate (where interaction with other subscribers is made available to you through the Platform); or</SubItem>
              <SubItem label="(f)">use data mining, robots, screen scraping or similar data gathering and extraction tools on the Platform for establishing, maintaining, advancing or reproducing information contained in the Platform, on your own website or in any other publication, or for your own personal use or benefit (other than as expressly set out in these terms of service), except with our prior written consent.</SubItem>
            </Clause>
            <Clause num="6.3.">
              You indemnify, and will defend and hold us and our affiliates,
              representatives, successors and assigns, including their applicable
              officers, directors, employees and agents, harmless in relation to any loss,
              claim or damage in connection with any claim by a third party related to
              your Data, your content or any of your users&rsquo; use of the Platform
              where such use is not authorised by these terms of service.
            </Clause>
          </Section>

          <Section num={7} title="Subscriptions">
            <Clause num="7.1.">
              There are different subscription plans for the Platform, with different
              inclusions and pricing, details of which are available.
            </Clause>
            <Clause num="7.2.">
              If you require any further customisation of the Platform for your specific
              needs, you may request us to provide Additional Services in accordance with
              clause 13.
            </Clause>
          </Section>

          <Section num={8} title="Recruiters and Candidates">
            <Clause num="8.1.">
              You can choose to subscribe to and use the Platform as a Recruiter or a
              Candidate.
            </Clause>
            <Clause num="8.2.">
              If you are a Recruiter, you can submit information about jobs and
              placements and engage with Candidates through the Platform, to assist with
              fulfilling a job or placement vacancy.
            </Clause>
            <Clause num="8.3.">
              If you are a Candidate, you can engage with Recruiters to accept and fulfill
              a job or placement vacancy.
            </Clause>
            <Clause num="8.4.">
              Our role is to facilitate digital introductions between Recruiters and
              Candidates. We are not a party to nor responsible for any arrangements that
              may arise between Recruiters and Candidates nor for any interactions that
              occur between Recruiters and Candidates, including any off-Platform
              agreements between Recruiters and Candidates. Once a Candidate accepts a
              job or placement associated with a Job Listing, a separate agreement is
              formed between the Candidate and the business that engages the Candidate
              for the completion of that job or placement. We are not a party to nor
              responsible for any agreements between the businesses that engage
              Candidates and Candidates.
            </Clause>
            <Clause num="8.5.">
              If you are a Recruiter, you agree that:
              <SubItem label="(a)">we will receive information about a job or placement from multiple Recruiters, sometimes for the same job or placement;</SubItem>
              <SubItem label="(b)">we use AI tools to ingest the information you provide to us about a job or placement, to deduplicate any information already received from other Recruiters about the same job or placement and to compile the information into a single Job Listing, and you consent to us ingesting, reproducing, reformating, modifying, merging, and displaying the information you provide to us about a job or placement as a Job Listing;</SubItem>
              <SubItem label="(c)">we may reject, modify or quarantine information submitted by you, including information about a job or placement, for example to strip personal or sensitive information from inbound emails;</SubItem>
              <SubItem label="(d)">once a Candidate registers on the Platform, they are independent and they themselves control their own profile data, and subject to clause 8.7, may choose which Recruiter to use for any particular Job Listing;</SubItem>
              <SubItem label="(e)">other Recruiters subscribe to and use the Platform, and if information about a job or placement is received from you and other Recruiters, your name may be displayed alongside the name of a competing Recruiter;</SubItem>
              <SubItem label="(f)">while we may use a simple API to verify the credentials of a Candidate, this does not constitute a background check, identity verification or guarantee of competency, and the Recruiter, and the business which wishes to offer a role to a Candidate, remain solely responsible for conducting all compliance and credential checks before offering the Candidate the role;</SubItem>
              <SubItem label="(g)">you are responsible for all information about Candidates that you access or download from the Platform, including responsibility for complying with your obligations in respect of that information under all applicable privacy and data protection legislation;</SubItem>
              <SubItem label="(h)">the Platform allows you to rate and provide feedback about Candidates, and that information may become known to other Recruiters;</SubItem>
              <SubItem label="(i)">
                we are not responsible in any way for the act or omissions of any Candidate or for any other matter in connection with any Job Listing, including any:
                <SubItem label="(i)" indent={0}>negligence;</SubItem>
                <SubItem label="(ii)" indent={0}>damage to property; or</SubItem>
                <SubItem label="(iii)" indent={0}>personal injury or death,</SubItem>
                <Box sx={{ mt: 1 }}>caused by a Candidate when performing any job or placement associated with a Job Listing; and</Box>
              </SubItem>
              <SubItem label="(j)">you release and indemnify us (as well as our affiliates, supplier, directors, officers, employees and agents) from any and all claims, liabilities, costs and expenses (including legal fees) arising in any way from these matters, except and to the extent where we are at fault.</SubItem>
            </Clause>
            <Clause num="8.6.">
              If you are a Candidate, you agree that:
              <SubItem label="(a)">we are not responsible in any way for the act or omissions of any Recruiter or business that offers you a role or for any other matter in connection with any Job Listing, including any delays in payments from any business that engages you for a job or placement associated with a Job Listing;</SubItem>
              <SubItem label="(b)">the Platform allows Recruiters to rate and provide feedback about you and other Candidates, and that information may become known to other Recruiters; and</SubItem>
              <SubItem label="(c)">you release and indemnify us (as well as our affiliates, supplier, directors, officers, employees and agents) from any and all claims, liabilities, costs and expenses (including legal fees) arising in any way from these matters, except and to the extent where we are at fault.</SubItem>
            </Clause>
            <Clause num="8.7.">
              The Platform provides Recruiters and Candidates with a limited exclusivity
              window, where:
              <SubItem label="(a)">a Candidate who subscribes to the Platform for the first time via a link connected with a Recruiter&rsquo;s subscriber account, will only be able to view, and submit applications for, Job Listings submitted by that Recruiter during the Exclusivity Period; and</SubItem>
              <SubItem label="(b)">the Candidate will be released into the &lsquo;general pool&rsquo; and will be able to view, and submit applications for, all Job Listings (including Job Listings unrelated to the Recruiter) immediately upon expiry of the Exclusivity Period.</SubItem>
            </Clause>
          </Section>

          <Section num={9} title="Data">
            <Clause num="9.1.">
              You are responsible for all Data which you input or is ingested into the
              Platform through your account or otherwise provide to us. You are solely
              responsible for determining the purposes and means of processing that Data,
              including Personal Information, by us under these terms of service, and
              must ensure that processing conducted according to your instructions will
              not place us in breach of any laws (including applicable data protection or
              privacy laws).
            </Clause>
            <Clause num="9.2.">
              You warrant that:
              <SubItem label="(a)">you have obtained all consents necessary to collect, store, disclose, use and transfer the Data, including any Personal Information included in the Data;</SubItem>
              <SubItem label="(b)">the Data will not violate or infringe the rights (including Intellectual Property Rights) of any other person;</SubItem>
              <SubItem label="(c)">the Data will not contain a virus or other harmful component; and</SubItem>
              <SubItem label="(d)">the Data will comply with any guidelines we notify to you from time to time on this website.</SubItem>
            </Clause>
            <Clause num="9.3.">
              If you are a Recruiter, you acknowledge that while we may employ data
              scrubbing technologies to produce and communicate a Job Listing on the
              Platform, you remain solely liable for the matters set out in clause 9.2.
            </Clause>
            <Clause num="9.4.">
              You are responsible for and must adopt reasonable measures to limit your
              exposure to the potential loss and damage of the Data, including secure
              storage of the source material. We expressly exclude liability for any loss
              of Data no matter how caused.
            </Clause>
            <Clause num="9.5.">
              You acknowledge that we may not be able to provide the Platform if the Data
              is not of a quality or condition suitable for processing based on our
              applicable standards, specifications and procedures or is otherwise not in
              the format we require.
            </Clause>
            <Clause num="9.6.">
              You acknowledge that we may use the Data and information derived from the
              Data to produce and exploit for our own benefit a compilation of aggregated
              data and information, provided that the Personal Information disclosed by
              you to us cannot be ascertained from the aggregated data and information.
              You agree we may use the Data and information derived from the Data,
              provided it is in a de-identified format, to train, improve, and refine our
              underlying machine learning models and data ingestion techniques.
            </Clause>
            <Clause num="9.7.">
              Subject to clause 9.8, you agree that we may, at our sole discretion,
              disclose, transfer and store the Data, including Personal Information, to
              or with our third party service providers and affiliates outside Australia
              for the purpose of providing the Platform or any part of it. The
              jurisdictions in which those Data transfers may occur are included in our{" "}
              <Link
                href="/privacy"
                sx={{ color: locumColors.tealDark, fontWeight: 600 }}
              >
                Privacy Policy
              </Link>
              . You consent to us providing Personal Information to those third party
              service providers and affiliates in those jurisdictions and (before
              submitting any Data to us) are solely responsible for ensuring that any
              disclosure, transfer or storage of Data, including Personal Information,
              across a country border under these terms of service complies with
              applicable data protection and privacy laws, including the Privacy Act.
            </Clause>
            <Clause num="9.8.">
              If you require that your Data (or specific portions of your Data) are only
              processed within a specific territory or jurisdiction (eg in Australia
              only), then we may agree with you in writing to only process that Data
              through the platform within those agreed territories or jurisdictions. You
              acknowledge that any customisation of Data handling may require the
              provision of an Additional Service and result in additional Fees, to be
              agreed between the parties.
            </Clause>
          </Section>

          <Section num={10} title="Viruses">
            <Clause num="10.1.">
              You must take your own precautions to ensure that the process which you use
              for accessing the Platform does not expose you to the risk of viruses,
              malicious computer code or other forms of interference which may damage
              your own computer system.
            </Clause>
            <Clause num="10.2.">
              We do not accept responsibility for any interference or damage to your own
              computer system which arises in connection with your use of the Platform or
              any linked website or service.
            </Clause>
          </Section>

          <Section num={11} title="Service Availability">
            <Clause num="11.1.">
              We intend to use reasonable endeavours to make the Platform available for
              your use on a 24 hour a day, seven days a week basis, for a minimum uptime
              of 99% per calendar month (measured in minutes) excluding downtime
              occurring as a result of:
              <SubItem label="(a)">routine or emergency maintenance;</SubItem>
              <SubItem label="(b)">upgrades or other development activities;</SubItem>
              <SubItem label="(c)">technical malfunctions of your software, equipment or infrastructure (e.g. telecommunications connectivity, network congestion or delays);</SubItem>
              <SubItem label="(d)">a Force Majeure event; or</SubItem>
              <SubItem label="(e)">due to clause 11.3 applying.</SubItem>
            </Clause>
            <Clause num="11.2.">
              In the case of technical problems which adversely affect your use of the
              Platform, you must make all reasonable efforts to investigate and diagnose
              problems before contacting us. If you still need technical help, you must
              notify us promptly via email to{" "}
              <Link
                href="mailto:techsupport@locumgroup.co"
                sx={{ color: locumColors.tealDark, fontWeight: 600 }}
              >
                techsupport@locumgroup.co
              </Link>
              .
            </Clause>
            <Clause num="11.3.">
              We may temporarily limit or suspend the availability of all or part of the
              Platform if it is necessary for reasons of public safety, security or
              maintenance of the Platform, interoperability of services, data protection
              or to perform work that is necessary for operational or technical reasons.
            </Clause>
          </Section>

          <Section num={12} title="Support">
            <Clause num="12.1.">
              We will provide you with access to the helpdesk from Monday to Friday
              inclusive between the hours of 8.30 to 5.00 pm AEST, except on Australian
              public holidays.
            </Clause>
          </Section>

          <Section num={13} title="Additional services">
            <Clause num="13.1.">
              You may ask us to provide Additional Services to you. If we agree to
              provide Additional Services to you, we may charge you for providing the
              Additional Services at our prevailing rates for those services as notified
              by us to you.
            </Clause>
          </Section>

          <Section num={14} title="Term">
            <Clause num="14.1.">
              These terms of service apply to your use of and access to the Platform from
              the date you first accept these terms of service until your subscription
              expires or is terminated.
            </Clause>
            <Clause num="14.2.">
              If you select a monthly subscription plan, your subscription will
              automatically rollover on a month by month basis, until you give us notice
              of your wish to cancel the subscription, which will take effect at the end
              of the then current monthly subscription term.
            </Clause>
            <Clause num="14.3.">
              If you select an annual subscription plan, your subscription will
              automatically rollover on a year by year basis, until you give us notice of
              your wish to cancel the subscription, which will take effect at the end of
              the then current annual subscription term.
            </Clause>
          </Section>

          <Section num={15} title="Fees and payment">
            <Clause num="15.1.">
              You must pay us the Fees for the Subscription you choose and any Additional
              Services (if applicable).
            </Clause>
            <Clause num="15.2.">
              Payment will be made by direct debit from your bank account or by charging
              your credit card each month, on or around the same day as the day you
              register to use the Platform and using the bank account or credit card
              details you provide when you register.
            </Clause>
            <Clause num="15.3.">
              We may change the Subscription Fee and Excess Volume Fee by giving you 30
              days&rsquo; notice of the change.
            </Clause>
            <Clause num="15.4.">
              All prices are in Australian dollars or such other currency as indicated.
            </Clause>
          </Section>

          <Section num={16} title="Failure to pay">
            <Clause num="16.1.">
              If you fail to pay any amount due under these terms of service by the due
              date, then:
              <SubItem label="(a)">all money owing to us by you becomes immediately payable;</SubItem>
              <SubItem label="(b)">we may suspend access to the Platform and may charge a reactivation fee for the suspended Service; and</SubItem>
              <SubItem label="(c)">you must pay all reasonable expenses incurred by us in enforcing these terms of service, including all expenses of any legal proceeding and all reasonable legal fees incurred in connection with any such action.</SubItem>
            </Clause>
            <Clause num="16.2.">
              Despite clause 16.1, if you fail to pay any amount due under these terms of
              service by the due date we may firstly try to charge your account:
              <SubItem label="(a)">1 business day after the due date; and</SubItem>
              <SubItem label="(b)">7 days after the due date,</SubItem>
              <Box sx={{ mt: 1 }}>before enforcing our rights under clause 16.1.</Box>
            </Clause>
          </Section>

          <Section num={17} title="GST and taxes">
            <Clause num="17.1.">
              You must reimburse us for all sales, use, transfer, privilege, excise or
              other taxes and all duties, whether international, national, state or
              local, however designated, which are levied or imposed by reason of the
              transactions contemplated under these terms of service, excluding, however,
              income taxes on profits which may be levied against us.
            </Clause>
            <Clause num="17.2.">
              Without limiting clause 17.1, all consideration provided for any taxable
              supply under these terms of service is exclusive of GST unless the contrary
              is clear. The amount of that consideration must be increased by an
              additional amount equal to the GST on that taxable supply. The party who
              has to pay the additional amount must pay it at the same time as the
              consideration in respect of that taxable supply becomes due.
            </Clause>
            <Clause num="17.3.">
              If at any time an adjustment is made or required to be made between a party
              and the relevant taxing authority on account of any amount paid as GST
              under these terms of service:
              <SubItem label="(a)">a corresponding adjustment must be made;</SubItem>
              <SubItem label="(b)">adjustment notes must be issued; and</SubItem>
              <SubItem label="(c)">any payment must be made,</SubItem>
              <Box sx={{ mt: 1 }}>between the parties as may be necessary to give effect to the adjustment.</Box>
            </Clause>
          </Section>

          <Section num={18} title="Intellectual Property Rights">
            <Clause num="18.1.">
              Except for the limited licence to access and use the Platform under clause
              3, all ideas, concepts, know-how, data processing techniques, data
              compilations, software, documentation, trade marks, trade secrets,
              copyright and inventions and other Intellectual Property Rights comprised
              in or in connection with the Platform (including its underlying technology,
              software, programs, as well as all its respective modifications,
              developments, updates and enhancements) are owned by us or our licensors.
              You agree that no transfer of our (or our licensors&rsquo;) Intellectual
              Property Rights occurs at any time by access and use by you (or your
              users&rsquo;) use of the Platform.
            </Clause>
            <Clause num="18.2.">
              You do not own or use, and must not claim any right or title to own or use,
              the Intellectual Property Rights in the Platform, except to the extent such
              use is permitted under these terms of service. You must not dispute or
              challenge our (or our licensors&rsquo;) entitlement to own, use or licence
              the Intellectual Property Rights in the Platform (including its underlying
              technology, software, programs, as well as all its respective
              modifications, developments, updates and enhancements) or join any third
              parties to challenge or contest the validity of those Intellectual Property
              Rights.
            </Clause>
            <Clause num="18.3.">
              You agree to notify us promptly of any infringement, or suspected or
              threatened infringement, of the Intellectual Property Rights in the
              Platform and reasonably co-operate with us in relation to such
              infringement.
            </Clause>
            <Clause num="18.4.">
              If you are a Recruiter, while you retain ownership to the original job and
              placement information you provide to us, including the Data ingested into
              the Platform through your account, you agree that we own the Intellectual
              Property Rights in the Job Listing and in the aggregated market insights
              derived from use and activity on the Platform. You assign to us, as a
              present assignment of future rights, all right, title and interest
              (including Intellectual Property Rights) you may own in and to the
              information which is comprised in the Job Listing and in the aggregated
              market insights derived from use and activity on the Platform. You agree
              to do all things and sign all documents necessary to perfect this
              assignment.
            </Clause>
          </Section>

          <Section num={19} title="Confidentiality">
            <Clause num="19.1.">
              Each party must not without the written consent of the other:
              <SubItem label="(a)">use any Confidential Information of the other party, except in performing its obligations under these terms of service; or</SubItem>
              <SubItem label="(b)">disclose any Confidential Information of the other party to any person except to its approved employees, officers, directors and other representatives, who need to review the Confidential Information in connection with performance under these terms of service, and then only to those who need to know the same and who agree to be bound by similar obligations of confidentiality. Despite the foregoing, we may disclose certain Confidential Information of yours to our third party suppliers, subject to the obligation of confidentiality, as necessary to (i) provide you with the Platform or obtain support for the Platform, (ii) obtain information specifically requested by you, (iii) perform back-office functions or administrative services, or (iv) as necessary to comply with legal and/or accounting requirements.</SubItem>
            </Clause>
            <Clause num="19.2.">
              Clause 19.1 does not apply where:
              <SubItem label="(a)">disclosure is required by law or required to respond to requests by a regulatory or judicial body;</SubItem>
              <SubItem label="(b)">the Confidential Information is in the public domain through no fault or action of the recipient, its employees or subcontractors; and</SubItem>
              <SubItem label="(c)">the Confidential Information was received by the recipient on a non-confidential basis from a third party who is not prohibited from disclosing it.</SubItem>
            </Clause>
            <Clause num="19.3.">
              These obligations of confidentiality survive termination of these terms of
              service.
            </Clause>
          </Section>

          <Section num={20} title="Privacy">
            <Clause num="20.1.">
              Each party must comply with any obligations they may have under the Privacy
              Act.
            </Clause>
            <Clause num="20.2.">
              All Personal Information provided to us is collected, used, and stored in
              accordance with our{" "}
              <Link href="/privacy" sx={{ color: locumColors.tealDark, fontWeight: 600 }}>
                privacy policy
              </Link>
              . By providing Personal Information, you consent to its collection, use,
              and disclosure as outlined in our privacy policy, which is available on our
              website &mdash; www.locumgroup.co.
            </Clause>
            <Clause num="20.3.">
              Each party warrants to the other that:
              <SubItem label="(a)">any Personal Information that it discloses to the other under these terms of service, or when using the Platform, has been collected in accordance with the Privacy Act and the individual to whom the information relates has consented to the disclosure;</SubItem>
              <SubItem label="(b)">the individual to whom the information relates has been made aware of the recipient&apos;s identity, of how to contact the recipient, and of the other matters of which the recipient is required to inform a person about whom it collects information under the Privacy Act; and</SubItem>
              <SubItem label="(c)">the other is authorised to collect the information for the disclosure and use the information for the purposes of these terms of service.</SubItem>
            </Clause>
            <Clause num="20.4.">
              Each party agrees that:
              <SubItem label="(a)">the Platform uses AI tools to ingest data to compile information obtained from various sources into Job Listings;</SubItem>
              <SubItem label="(b)">while the AI tools used, including large language models, have been trained to exclude Personal Information, it is possible that some Personal Information may be included in a Job Listing &mdash; this is one of the inherent risks of using the Platform;</SubItem>
              <SubItem label="(c)">it will notify us promptly of any Job Listings that include Personal Information, so that corrective action can be taken to remove Personal Information from the Job Listing; and</SubItem>
              <SubItem label="(d)">it releases us from any claims that it might have, or had, against us, whether at common law or under legislation, in connection with the erroneous inclusion of Personal Information in a Job Listing, it being understood that this does not absolve our obligation under clause 20.1 to comply with the Privacy Act and any other applicable laws and codes dealing with privacy.</SubItem>
            </Clause>
          </Section>

          <Section num={21} title="Indemnity">
            <Clause num="21.1.">
              You agree to hold harmless and indemnify us and our officers, agents and
              employees against any loss, damage, costs or expenses that we, or any of
              our officers, agents and employees, may incur in connection with your
              breach of these terms of service or any other legal obligation, your use of
              the Platform or any use of the Platform using an account registered in your
              name, except to the extent caused by us.
            </Clause>
            <Clause num="21.2.">
              The indemnity in this clause:
              <SubItem label="(a)">is a continuing obligation, separate and independent from the other obligations of the parties;</SubItem>
              <SubItem label="(b)">will not be affected by any matter including without limitation, the termination, renewal or extension of these terms of service or any indulgence, waiver or other concession given by us unless we agree in writing; and</SubItem>
              <SubItem label="(c)">includes legal costs and disbursements on a full indemnity basis.</SubItem>
            </Clause>
            <Clause num="21.3.">
              It is not necessary for us to incur expense or to make any payment before
              enforcing the right of indemnity conferred by this clause.
            </Clause>
            <Clause num="21.4.">
              You must pay on demand any amount you must pay under the indemnity in this
              clause.
            </Clause>
          </Section>

          <Section num={22} title="Force Majeure">
            <Clause num="22.1.">
              You acknowledge that making the Platform available for access is dependent
              on a number of factors outside our control, including for example, the
              telecommunication connections and infrastructure.
            </Clause>
            <Clause num="22.2.">
              Except for any payment obligations, neither party is liable for any delay
              or failure to perform any of its obligations under these terms of service
              to the extent that the delay or failure is caused by reason of Force
              Majeure.
            </Clause>
          </Section>

          <Section num={23} title="Warranties">
            <Clause num="23.1.">
              Each party represents and warrants to the other that:
              <SubItem label="(a)">it has the power and authority to enter into and perform its obligations under these terms of service;</SubItem>
              <SubItem label="(b)">it has taken all necessary steps, including any corporate action necessary if it is a corporation, to authorise its entry into and performance of all of its obligations under these terms of service and to carry out the transactions contemplated by these terms of service.</SubItem>
            </Clause>
          </Section>

          <Section num={24} title="Disclaimer">
            <Clause num="24.1.">
              You are responsible for authorising any person who is given access to the
              Platform using your subscriber account and to the Data. You agree that we
              have no obligation to provide any person access to the Platform through
              your subscriber account or to the Data without your authorisation.
            </Clause>
            <Clause num="24.2.">
              We make no representation or warranty:
              <SubItem label="(a)">that your use of the Platform will be error-free, uninterrupted or compatible with your equipment, devices and software configurations;</SubItem>
              <SubItem label="(b)">that the Platform will be fit for your purposes; or</SubItem>
              <SubItem label="(c)">that your use of the Platform will improve the performance or profitability of your business or any other party.</SubItem>
            </Clause>
            <Clause num="24.3.">
              You agree that:
              <SubItem label="(a)">the provision of, access to, and use of, the Platform is on an &ldquo;as is&rdquo; basis and at your own risk;</SubItem>
              <SubItem label="(b)">among other things, the operation and availability of the systems used for accessing the Platform, including telecommunication services, computer networks and the Internet, can be unpredictable and may from time to time interfere with or prevent access to the Platform. We are not in any way responsible for any interference or prevention of your access or use of the Platform caused by these types of things;</SubItem>
              <SubItem label="(c)">it is your sole responsibility to determine that the Platform meets the needs of your business and is suitable for the purposes for which it is used;</SubItem>
              <SubItem label="(d)">we may modify, add or remove features or algorithms at any time, provided that we do not diminish the operability of the Platform; and</SubItem>
              <SubItem label="(e)">you remain solely responsible for complying with all applicable laws in connection with your access and use of the Platform.</SubItem>
            </Clause>
          </Section>

          <Section num={25} title="Limitation of liability">
            <Clause num="25.1.">
              Nothing in these terms of service is or should be interpreted as an attempt
              to modify, limit or exclude any right or remedy, or any guarantee, term,
              condition, warranty, undertaking, inducement or representation, implied or
              imposed by legislation which cannot be modified, limited or excluded.
            </Clause>
            <Clause num="25.2.">
              You agree that you do not rely on any guarantee, term, condition, warranty,
              undertaking, inducement or representation made by us or on our behalf which
              is not expressly stated in these terms of service. In particular, we make
              no warranty or representation that:
              <SubItem label="(a)">this website or the Platform will always be available, accessible, secure or operate without error;</SubItem>
              <SubItem label="(b)">the Platform will be fit for your purposes; or</SubItem>
              <SubItem label="(c)">use of the Platform will provide any financial benefit to you.</SubItem>
            </Clause>
            <Clause num="25.3.">
              Where any legislation implies or imposes a guarantee, term, condition,
              warranty, undertaking, inducement or representation in relation to these
              terms of service and we are not able to exclude that guarantee, term,
              condition, warranty, undertaking, inducement or representation (
              <strong>Non-Excludable Provision</strong>), and we are able to limit your
              remedy for a breach of the Non-Excludable Provision, then our liability for
              breach of the Non-Excludable Provision is limited to (at our election):
              <SubItem label="(a)">in the case of goods, the repair or replacement of the goods or the supply of equivalent goods (or the payment of the cost of doing so); and</SubItem>
              <SubItem label="(b)">in the case of services, the supplying of the services again (or the payment of the cost of doing so).</SubItem>
            </Clause>
            <Clause num="25.4.">
              Subject to our obligations under the Non-Excludable Provisions:
              <SubItem label="(a)">if you are a Recruiter, our maximum aggregate liability to you in respect of any one claim or series of connected claims in connection with these terms of service or your use of this website or the Platform, whether arising in or for breach of contract, negligence or other tort, breach of statutory duty, or under an indemnity or otherwise is limited to the Subscription Fee payable by you during your first 6 months of subscription;</SubItem>
              <SubItem label="(b)">if you are a Candidate, our maximum aggregate liability to you in respect of any one claim or series of connected claims in connection with these terms of service or your use of this website or the Platform, whether arising in or for breach of contract, negligence or other tort, breach of statutory duty, or under an indemnity or otherwise is limited to $100; and</SubItem>
              <SubItem label="(c)">we are not liable to you or any third party for any Indirect Loss arising in connection with any use or access, or any inability to use or access, or misuse by you or any other party, of this website or the Platform or otherwise in connection with any Content, whether arising in or for breach of contract, negligence or other tort, breach of statutory duty, breach of warranty or guarantee, under an indemnity or otherwise and whether or not that loss was foreseeable, even if we have been advised of the possibility of such loss.</SubItem>
            </Clause>
            <Clause num="25.5.">
              The liability of a party for loss or damage sustained by the other party
              will be reduced proportionately to the extent that:
              <SubItem label="(a)">such loss or damage has been caused by the other party&apos;s failure to comply with its obligations and responsibilities under these terms of service; or</SubItem>
              <SubItem label="(b)">the negligence of the other party has contributed to such loss or damage, regardless of whether a claim is made by the other party.</SubItem>
            </Clause>
          </Section>

          <Section num={26} title="Suspension">
            <Clause num="26.1.">
              In addition to our right to suspend your access under clause 11.3, we may
              without notice suspend your access to the Platform if:
              <SubItem label="(a)">you (or your Administrators or any of your Authorised Users) breach these terms of service; or</SubItem>
              <SubItem label="(b)">we reasonably believe that your access and use of the Platform will cause technical incapacity to the Platform which will continue unless access or use is suspended; or</SubItem>
              <SubItem label="(c)">we reasonably believe that you are using the Platform in a manner that adversely affects the use of the Platform by other users, or you have breached the law.</SubItem>
            </Clause>
            <Clause num="26.2.">
              We will act promptly to restore access when it is satisfied that the reason
              for suspension has been removed.
            </Clause>
          </Section>

          <Section num={27} title="Dispute resolution procedure">
            <Clause num="27.1.">
              Subject to clause 27.6, if a Dispute arises in connection with these terms
              of service the parties must first, acting in good faith, use their
              reasonable endeavours to resolve the dispute in accordance with the
              procedure in this clause 27.
            </Clause>
            <Clause num="27.2.">
              A party claiming that a Dispute has arisen under, or in connection with,
              these terms of service (the <strong>Disputing Party</strong>) must give to
              the other party a notice identifying precisely, and providing full
              particulars of, that Dispute, including details of any loss suffered by
              that party in relation to the Dispute (a <strong>Dispute Notice</strong>).
            </Clause>
            <Clause num="27.3.">
              Each party to the Dispute must use its reasonable endeavours to resolve the
              Dispute within 10 Business Days of the issue of the Dispute Notice.
            </Clause>
            <Clause num="27.4.">
              If the Dispute has not been resolved by the parties within 10 Business Days
              of the issue of the Dispute Notice, each party agrees that the Dispute
              must be referred for mediation in accordance with the Mediation Rules of
              the Law Society of New South Wales to:
              <SubItem label="(a)">a mediator agreed on by the parties; or</SubItem>
              <SubItem label="(b)">if the parties are unable to agree on a mediator within a further five Business Days, a mediator nominated by the then current President of the Law Society of New South Wales.</SubItem>
            </Clause>
            <Clause num="27.5.">
              Each party must bear its own costs of complying with this clause and the
              parties to the Dispute must bear equally the costs of the mediator.
            </Clause>
            <Clause num="27.6.">
              This clause 27 does not apply where a party wishes to commence proceedings
              for urgent interlocutory or declaratory relief (or other interim or
              conservatory measures, including measures required to prevent expiry of a
              relevant limitation period).
            </Clause>
          </Section>

          <Section num={28} title="Termination">
            <Clause num="28.1.">
              Subject to clauses 14.1 and 14.2, in addition and without prejudice to any
              other rights or remedies, either party may terminate these terms of service
              as follows:
              <SubItem label="(a)">by giving the other party 30 days written notice of termination; or</SubItem>
              <SubItem label="(b)">
                immediately upon giving written notice to the other party, if the other party becomes or threatens to become subject to any form of insolvency administration and that insolvency administration:
                <SubItem label="(i)" indent={0}>does not give rise to a Stay; or</SubItem>
                <SubItem label="(ii)" indent={0}>gives rise to a Stay, but a court makes an order to lift that Stay.</SubItem>
              </SubItem>
            </Clause>
          </Section>

          <Section num={29} title="Effects of termination">
            <Clause num="29.1.">
              On termination of these terms of service for any reason, you must:
              <SubItem label="(a)">subject to clause 29.3, immediately cease accessing and using the Platform; and</SubItem>
              <SubItem label="(b)">immediately pay any and all outstanding invoices or any charges or fees incurred as a result of your access and use of the Platform before the date of termination.</SubItem>
            </Clause>
            <Clause num="29.2.">
              Termination of these terms of service does not affect any accrued rights or
              liabilities of any party under these terms of service nor does it affect
              any provision of these terms of service which is expressly or by
              implication intended to operate after termination.
            </Clause>
            <Clause num="29.3.">
              For a period of 30 days after termination of these terms of service, we
              will continue to provide you with access to the Platform, solely for the
              purposes of allowing you to extract any Data that you have saved in the
              Platform, following which we will permanently delete any Data you have
              saved in the Platform.
            </Clause>
          </Section>

          <Section num={30} title="Notices">
            <Clause num="30.1.">
              Notices given under these terms of service must be in writing and delivered
              to a party by hand or by email to that party&rsquo;s address shown below or
              to an alternate address notified to the party giving the notice. Your
              address for delivery of a notice is the address notified to us when you
              register to use the Platform. Our address for delivery of a notice is:
              <Box sx={{ mt: 2, color: locumColors.textPrimary, lineHeight: 1.8 }}>
                <strong>Company Secretary</strong>
                <br />
                Locumplus Pty Ltd
                <br />
                Email:{" "}
                <Link
                  href="mailto:info@locumgroup.co"
                  sx={{ color: locumColors.tealDark, fontWeight: 600 }}
                >
                  info@locumgroup.co
                </Link>
              </Box>
            </Clause>
            <Clause num="30.2.">
              A notice is taken to be duly given and received:
              <SubItem label="(a)">if delivered by hand, when delivered; or</SubItem>
              <SubItem label="(b)">if delivered by email, when the recipient party confirms, by non-automated email, receipt of the notice.</SubItem>
            </Clause>
          </Section>

          <Section num={31} title="Variations">
            <Clause num="31.1.">
              We may amend or update these terms of service from time to time.
            </Clause>
            <Clause num="31.2.">
              If any change to these terms of service will significantly affect the
              provision of the Platform we will give you at least 30 days notice of the
              change by email.
            </Clause>
            <Clause num="31.3.">
              If you do not agree to any change to these terms of service, you can
              terminate your Subscription to the Platform by giving us at least 14 days
              written notice.
            </Clause>
            <Clause num="31.4.">
              You agree:
              <SubItem label="(a)">to periodically check this website to ensure that you understand the requirements of the current terms of service; and</SubItem>
              <SubItem label="(b)">that your continued use of the Platform will represent an agreement by you to be bound by the terms of service as amended.</SubItem>
            </Clause>
          </Section>

          <Section num={32} title="General">
            <Clause num="32.1.">
              These terms of service constitutes the entire understanding between the
              parties and supersedes all previous and contemporaneous communications,
              representations, or agreements with respect to your access and use of the
              Platform.
            </Clause>
            <Clause num="32.2.">
              No failure or delay of either party in exercising any right, power, or
              privilege under these terms of service (and no course of dealing between
              the parties) operates as a waiver of any such right, power of privilege.
              No waiver of any default on any one occasion constitutes a waiver of any
              subsequent default. No single or partial exercise of any right, power, or
              privilege precludes the further or full exercise of such right, power or
              privilege.
            </Clause>
            <Clause num="32.3.">
              If any provision of these terms of service is held to be unenforceable, the
              parties agree to substitute the affected provision with an enforceable
              provision that approximates the intent and economic effect of the affected
              provision.
            </Clause>
            <Clause num="32.4.">
              If any one or more of the provisions of these terms of service are for any
              reason held to be invalid, illegal or unenforceable by a court of competent
              jurisdiction or a panel of arbitrators, the remaining provisions of these
              terms of service will be unimpaired and will remain in full force and
              effect.
            </Clause>
            <Clause num="32.5.">
              Each party must do everything reasonably required by the other to give full
              effect to these terms of service.
            </Clause>
            <Clause num="32.6.">
              These terms of service and all matters regarding the interpretation and/or
              enforcement of these terms of service, are governed exclusively by the laws
              in force in the State of New South Wales, Australia and the parties submit
              to the non-exclusive jurisdiction of the courts of New South Wales.
            </Clause>
          </Section>

          <Box
            sx={{
              mt: 8,
              p: { xs: 3, md: 4 },
              bgcolor: locumColors.tealLight,
              borderRadius: "8px",
              border: `1px solid ${locumColors.tealBorderSubtle}`,
            }}
          >
            <Typography variant="h4" sx={headingSx}>Questions?</Typography>
            <Typography sx={bodySx}>
              For questions about these terms, contact us at:
            </Typography>
            <Typography sx={{ color: locumColors.textPrimary, lineHeight: 1.8, mb: 0 }}>
              <strong>Company Secretary</strong>
              <br />
              LocumPlus Pty Ltd
              <br />
              Email:{" "}
              <Link
                href="mailto:info@locumgroup.co"
                sx={{ color: locumColors.tealDark, fontWeight: 600 }}
              >
                info@locumgroup.co
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>
      {!legalOnly && <Footer />}
    </>
  );
}
